import { hasScope } from '@nyce/admin-kit/api';
import { SCOPES } from '../../api';
import { useAsync } from '../../components/common';
import { useHubLogin } from '../../session';
import { formatCount, formatRelative } from '../../lib/format';
import type { Issue } from '../../pages/dashboard/shared';
import { PRODUCTION_PATH, prodStats, type ProdTotals } from '../api/prodstats';
import { headline } from '../series';

// Production at a glance, for the main dashboard and the Statistics group dashboard: yesterday's and the last 30 days'
// totals, and how the nightly count went.

export function useProductionSummary() {
  const enabled = hasScope(useHubLogin()?.scopes, SCOPES.dbRead);
  const state = useAsync(
    async (): Promise<ProdTotals | null> => (enabled ? prodStats.totals() : null),
    [enabled]
  );
  return { ...state, enabled };
}

/** A nightly count that failed, left databases out, or has not finished for two days. */
export function productionIssues(t: ProdTotals, now = Date.now()): Issue[] {
  const out: Issue[] = [];
  const last = t.lastFinished;
  if (last?.status === 'failed')
    out.push({
      key: 'prod:failed',
      status: 'error',
      name: 'Production statistics',
      what: 'nightly count',
      detail: `run #${last.id} failed${last.error ? `: ${last.error}` : ''}`,
      to: PRODUCTION_PATH,
    });
  else if (last && last.databasesFailed > 0)
    out.push({
      key: 'prod:partly',
      status: 'warn',
      name: 'Production statistics',
      what: 'nightly count',
      detail: `${last.databasesFailed} of ${last.databasesTotal} databases could not be counted in run #${last.id}`,
      to: PRODUCTION_PATH,
    });
  const finished = last?.finishedAt ? Date.parse(last.finishedAt) : null;
  if (t.schedule.enabled && t.firstDay && (finished == null || now - finished > 2 * 86_400_000))
    out.push({
      key: 'prod:stale',
      status: 'warn',
      name: 'Production statistics',
      what: 'nightly count',
      detail: `no count finished since ${formatRelative(last?.finishedAt ?? null)}`,
      to: PRODUCTION_PATH,
    });
  return out;
}

/** The group card's lines: yesterday and the last 30 days, then the last count. */
export function productionLines(t: ProdTotals) {
  if (!t.firstDay) return [<>Nothing counted yet</>];
  const y = headline(t.metrics, (m) => m.yesterday);
  const m = headline(t.metrics, (x) => x.last30);
  return [
    <>
      Yesterday <b>{formatCount(y.transactions)}</b> transactions · <b>{formatCount(y.deliveries)}</b>{' '}
      deliveries · <b>{formatCount(y.lines)}</b> lines
    </>,
    <>
      30 days <b>{formatCount(m.transactions)}</b> transactions · <b>{formatCount(m.deliveries)}</b>{' '}
      deliveries · <b>{formatCount(m.shipments)}</b> shipment references
    </>,
    t.lastFinished && <>Counted {formatRelative(t.lastFinished.finishedAt)}</>,
  ];
}
